import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CurrencyCode, DEFAULT_CURRENCY, EXCHANGE_RATES_TTL_MS } from "@/lib/config/currency";
import { currencyService, ExchangeRatesSnapshot } from "@/lib/services/currency.service";

interface CurrencyContextValue {
  currency: CurrencyCode;
  rates: ExchangeRatesSnapshot["rates"];
  isRatesLoading: boolean;
  setCurrency: (currency: CurrencyCode) => void;
  refreshRates: () => Promise<void>;
  convertPrice: (amountMad: number) => number;
  formatPrice: (amountMad: number, options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const FALLBACK_RATES: ExchangeRatesSnapshot["rates"] = {
  MAD: 1,
  EUR: 0.092,
  USD: 0.099,
  CAD: 0.135,
  GBP: 0.078,
  AED: 0.363,
};

export const CurrencyProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const [currency, setCurrencyState] = useState<CurrencyCode>(DEFAULT_CURRENCY);
  const [rates, setRates] = useState<ExchangeRatesSnapshot["rates"]>(FALLBACK_RATES);
  const [isRatesLoading, setIsRatesLoading] = useState(true);

  const setCurrency = useCallback((nextCurrency: CurrencyCode) => {
    setCurrencyState(nextCurrency);
    currencyService.persistCurrency(nextCurrency);
  }, []);

  const refreshRates = useCallback(async () => {
    setIsRatesLoading(true);
    try {
      const snapshot = await currencyService.getExchangeRates(false);
      setRates(snapshot.rates);
    } finally {
      setIsRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = currencyService.getStoredCurrency();
    if (stored !== DEFAULT_CURRENCY) {
      setCurrencyState(stored);
      return;
    }

    const autoDetected = currencyService.detectCurrencyFromUserEnvironment();
    setCurrencyState(autoDetected);
    currencyService.persistCurrency(autoDetected);
  }, []);

  useEffect(() => {
    void refreshRates();
    const timer = window.setInterval(() => {
      void refreshRates();
    }, EXCHANGE_RATES_TTL_MS);
    return () => window.clearInterval(timer);
  }, [refreshRates]);

  const convertPrice = useCallback(
    (amountMad: number) => currencyService.convertPrice(amountMad, currency, rates),
    [currency, rates]
  );

  const formatPrice = useCallback(
    (
      amountMad: number,
      options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
    ) => {
      return currencyService.formatPriceFromMad(amountMad, currency, i18n.language, rates, options);
    },
    [currency, i18n.language, rates]
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      rates,
      isRatesLoading,
      setCurrency,
      refreshRates,
      convertPrice,
      formatPrice,
    }),
    [currency, rates, isRatesLoading, setCurrency, refreshRates, convertPrice, formatPrice]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export { CurrencyContext };
